import { useUnityBuildStore } from '../stores/store';
import {
  useCreateProjectEstimateAssemblyMutation,
  useUpdateProjectEstimateAssemblyMutation,
} from './useCreateProjectEstimateAssemblyMutation';
import {
  useCreateContractChangeOrderAssemblyMutation,
  useUpdateContractChangeOrderAssemblyMutation,
} from './useCreateContractChangeOrderAssemblyMutation';
import { useQueryClient } from 'react-query';
import {
  EstimateAssembly,
  EstimateBreakoutMap,
} from '../api/protosCompiled/estimateAssembly/estimateAssembly_pb';
import {
  ChangeOrderAssembly,
  ChangeOrderBreakoutMap,
} from '../api/protosCompiled/changeOrderAssembly/changeOrderAssembly_pb';
import { Calculations } from '../utils/calculations';
import { useStore } from 'zustand';
import { useSyncInvalidation } from './useSyncConnector';

import { calculateSegmentCount } from '../api/ProjectEstimateAssemblyService';
import {
  calculatePolygonArea,
  convertPointToRealWorld,
} from '../components/svgElements/takeoffHelper';
import { TakeoffAssemblies } from '../types/AssemblyItemType';
import { useAssemblyStore } from '../stores/AssemblyStore';
import { markAssemblyUpdating, markAssemblyUpdated } from '../utils/assemblyUpdateTracker';
import { ResetStates } from '../stores/resetStates';
import { useKeyBoardEventStore } from '../stores/keyEventStore';

type submitAssemblyProps = {
  assembly: EstimateAssembly.AsObject | ChangeOrderAssembly.AsObject;
  /**
   * Controls whether breakout information should be updated.
   * Should be true ONLY when submitting from CreateAssembly.tsx.
   * All other components should set this to false to preserve existing breakout information.
   */
  shouldUpdateBreakout: boolean;
  pageScale: number;
  isCreating?: boolean;
  isUpdating?: boolean;
  shouldClearCurrentTakeoffAssembly?: boolean;
};

export const useSubmitTakeoffAssembly = () => {
  const queryClient = useQueryClient();
  const { invalidateSync } = useSyncInvalidation();
  const unityBuildStore = useStore(useUnityBuildStore);
  const globalAssemblyStore = useStore(useAssemblyStore);
  const keyboardEventStore = useStore(useKeyBoardEventStore);
  const {
    selectedBreakout,
    selectedEstimate,
    selectedContract,
    selectedChangeOrder,
    selectedProject,
    currentPage,
  } = useStore(useUnityBuildStore);

  const { mutateAsync: createEstimateAssembly } = useCreateProjectEstimateAssemblyMutation();
  const { mutateAsync: updateEstimateAssembly } = useUpdateProjectEstimateAssemblyMutation();
  const { mutateAsync: createChangeOrderAssembly } = useCreateContractChangeOrderAssemblyMutation();
  const { mutateAsync: updateChangeOrderAssembly } = useUpdateContractChangeOrderAssemblyMutation();

  const queryKey = [
    'countedAssemblies',
    selectedEstimate?.estimateid || selectedChangeOrder?.changeorderid,
  ];

  const submitAssembly = async (props: submitAssemblyProps) => {
    // Track assembly as updating BEFORE we start to gate selection elsewhere
    markAssemblyUpdating((props.assembly as any)?.assemblyid);

    // Start overall performance tracking for this function
    try {
      if (!selectedBreakout) {
        throw new Error('invalid request: no breakout provided');
      }

      if (props.isCreating && props.isUpdating) {
        if (props.isCreating === props.isUpdating) {
          console.error(
            `isCreating is ${props.isCreating} and isUpdating is ${props.isUpdating}, these two values cannot be the same`
          );
          return;
        }
        if (!selectedProject && !selectedContract) {
          console.error('Cannot create assembly without a project or contract selected');
          return;
        }
      }

      // Calculate measurements (pointCount should exclude points where isincludedincount is false)
      const pointCount = props.assembly.pointsList.filter(point => point.isincludedincount).length;
      const calculatedTotalLength = Calculations.calculateTotalDistance(
        props.assembly.pointsList,
        props.pageScale,
        props.assembly.assemblymeasurementtype as 'length' | 'area'
      );
      const segmentCount = calculateSegmentCount(props.assembly);
      const sqft =
        props.assembly.assemblymeasurementtype === 'area'
          ? calculatePolygonArea(
              props.assembly.pointsList.map(point =>
                convertPointToRealWorld(point, props.pageScale)
              )
            )
          : 0;

      // Prepare the optimistic update
      const optimisticAssembly: TakeoffAssemblies = {
        ...props.assembly,
        parentid: '',
        estimateid: '',
        createdbyuserid: '',
        createdbyaccountid: '',
        lastupdated: '',
        assembliesList: [
          {
            ...props.assembly,
            pointcount: pointCount,
            segmentlength: calculatedTotalLength,
            segmentcount: segmentCount,
            measuredarea: sqft,
            userscale: props.pageScale,
            pagenumber: currentPage,
            itemsList: (props.assembly.itemsList || []).map(item => {
              // Only update breakout information if shouldUpdateBreakout is true
              // If false, preserve the existing breakout information from the item
              if (props.shouldUpdateBreakout) {
                return {
                  ...item,
                  breakout: {
                    breakoutmapid: item.breakout?.breakoutmapid,
                    breakoutid: selectedBreakout.id,
                    breakoutname: selectedBreakout.breakoutname,
                    multiplier: selectedBreakout.multiplier,
                    typicalbreakoutid:
                      (selectedBreakout as any)?._typicalBreakoutId ||
                      item.breakout?.typicalbreakoutid ||
                      '',
                  } as EstimateBreakoutMap.AsObject | ChangeOrderBreakoutMap.AsObject,
                };
              } else {
                // Preserve the existing breakout
                return { ...item };
              }
            }),
          },
        ],
      };

      // Cancel ongoing queries for the specific key to prevent race conditions
      await queryClient.cancelQueries(queryKey);

      // Capture the previous state for rollback if needed
      const previousData = queryClient.getQueryData(queryKey);

      // Apply optimistic update to the raw assemblies data
      const updateQueryCache = (key: unknown[]) => {
        queryClient.setQueryData(key, (oldRawAssemblies: any[] | undefined) => {
          // Ensure oldRawAssemblies is not undefined
          const old = oldRawAssemblies || [];

          // Make a deep copy to avoid reference issues
          const assembliesToUpdate = JSON.parse(JSON.stringify(old));

          if (props.isCreating) {
            // For creation, add the new assembly to the appropriate takeoff
            // Find the first takeoff and add the assembly to its assembliesList
            if (assembliesToUpdate.length > 0) {
              const firstTakeoff = assembliesToUpdate[0];
              return [
                {
                  ...firstTakeoff,
                  assembliesList: [...firstTakeoff.assembliesList, props.assembly],
                },
              ];
            } else {
              // Create a new takeoff if none exists
              return [
                {
                  assembliesList: [props.assembly],
                  // Add other required fields as needed
                },
              ];
            }
          } else if (props.isUpdating) {
            // For updates, find the assembly by its ID and update it
            return assembliesToUpdate.map((takeoff: any) => ({
              ...takeoff,
              assembliesList: takeoff.assembliesList.map((assembly: any) =>
                assembly.assemblyid === props.assembly.assemblyid ? props.assembly : assembly
              ),
            }));
          }
          return assembliesToUpdate;
        });
      };

      // Update the raw assemblies cache
      updateQueryCache(queryKey);

      try {
        if (selectedEstimate && selectedProject) {
          if (props.isCreating) {
            await createEstimateAssembly({
              ...optimisticAssembly,
              parentid: selectedProject.id,
              estimateid: selectedEstimate.estimateid,
            });
          } else if (props.isUpdating) {
            await updateEstimateAssembly({
              ...optimisticAssembly,
              parentid: selectedProject.id,
              estimateid: selectedEstimate.estimateid,
            });
          }

          if (queryKey) {
            try {
              await invalidateSync(queryKey);
            } catch (error) {
              console.warn('Failed to invalidate estimate query:', error);
              // Fall back to direct query invalidation if sync invalidation fails
              queryClient.refetchQueries(queryKey);
            }
          }
        } else if (selectedChangeOrder && selectedContract) {
          if (props.isCreating) {
            await createChangeOrderAssembly({
              ...optimisticAssembly,
              parentid: selectedContract.contractid,
              estimateid: selectedChangeOrder.changeorderid,
            });
          } else if (props.isUpdating) {
            await updateChangeOrderAssembly({
              ...optimisticAssembly,
              parentid: selectedContract.contractid,
              estimateid: selectedChangeOrder.changeorderid,
            });
          }

          if (queryKey) {
            try {
              await invalidateSync(queryKey);
            } catch (error) {
              console.warn('Failed to invalidate change order query:', error);
              // Fall back to direct query invalidation if sync invalidation fails
              queryClient.refetchQueries(queryKey);
            }
          }
        }

        // Clear currentTakeoffAssembly after successful save, unless we should preserve it
        // The optimistic cache update ensures the UI doesn't flicker
        if (props.shouldClearCurrentTakeoffAssembly !== false) {
          ResetStates.resetTakeoffStates(unityBuildStore, globalAssemblyStore, keyboardEventStore);
        }

        // Notify sync system of updates without immediately invalidating
        try {
          await invalidateSync(queryKey);
        } catch (error) {
          console.warn('Failed to synchronize base query invalidation:', error);
          // Continue with execution even if sync fails
        }

        // IMPORTANT: Instead of immediate invalidation which causes flickering,
        // schedule a background refresh after a short delay to ensure UI stability
        setTimeout(() => {
          try {
            // This will refresh the data in the background without clearing the cache first
            queryClient.invalidateQueries(queryKey);
          } catch (error) {
            console.warn('Scheduled query invalidation failed:', error);

            // If there's an auth failure, try a direct refetch without invalidation
            try {
              queryClient.refetchQueries(queryKey, { exact: true });
            } catch (refetchError) {
              console.error('Refetch fallback also failed:', refetchError);
            }
          }
        }, 200); // Reduced from 500ms to 200ms for faster UI sync

        // After a short grace period, clear the updating flag to re-enable UI actions
        setTimeout(() => {
          markAssemblyUpdated((props.assembly as any)?.assemblyid);
        }, 700);
      } catch (error) {
        // Rollback optimistic updates if the server request fails
        console.error('Server request failed, rolling back optimistic update:', error);
        if (previousData) {
          queryClient.setQueryData(queryKey, previousData);
        }

        // After rollback, invalidate to ensure data consistency
        queryClient.invalidateQueries(queryKey);
        // Ensure we clear the updating flag on failure to avoid a stuck state
        markAssemblyUpdated((props.assembly as any)?.assemblyid);
        throw error;
      }
    } catch (error) {
      console.error('Error in submitAssembly:', error);
      // Clear updating flag on unexpected errors as well
      markAssemblyUpdated((props.assembly as any)?.assemblyid);
    }
  };

  return { submitAssembly, queryKey: queryKey };
};
